import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContent, PageHeader, PageSection } from 'common/styles/page';
import Tabs from 'common/components/Tabs';
import { isCurrentUserSuperUser } from 'common/selectors/auth';
import { AIPromptEditor } from '../../ai-settings/components';
import { FeatureFlagList } from '../../feature-flags/components/FeatureFlagList';
import useFeatures from 'common/hooks/useFeatures';
import { PerformanceMetricsManager } from '../../performance-metrics/components/PerformanceMetricsManager';

const TAB_SLUGS = {
  metrics: 'Metrics',
  'ai-settings': 'AI Settings',
  features: 'Features',
} as const;

type TabSlug = keyof typeof TAB_SLUGS;

const slugFromLabel = (label: string): TabSlug =>
  (Object.entries(TAB_SLUGS).find(([, v]) => v === label)?.[0] as TabSlug) ?? 'ai-settings';

const SystemSetup: React.FC = () => {
  const { tab: urlTab } = useParams<{ tab?: string }>();
  const navigate = useNavigate();
  const isSuperUser = useSelector(isCurrentUserSuperUser);
  const { initialized: featuresInitialized } = useFeatures();
  const isMetricsEnabled = featuresInitialized;

  // Build tabs array: Metrics appears first when the Culture Hub feature is enabled
  const tabs = [
    ...(isMetricsEnabled ? ['Metrics'] : []),
    'AI Settings',
    ...(isSuperUser ? ['Features'] : []),
  ];

  const defaultSlug: TabSlug = isMetricsEnabled ? 'metrics' : 'ai-settings';
  const activeSlug = (urlTab as TabSlug) in TAB_SLUGS ? (urlTab as TabSlug) : defaultSlug;
  const activeLabel = TAB_SLUGS[activeSlug];

  // Validate slug: redirect if missing, invalid, or pointing to a hidden tab
  useEffect(() => {
    const isHidden =
      (activeSlug === 'metrics' && !isMetricsEnabled) ||
      (activeSlug === 'features' && !isSuperUser);

    if (!urlTab || !(urlTab in TAB_SLUGS) || isHidden) {
      navigate(`/settings/admin/system-setup/${defaultSlug}`, { replace: true });
    }
  }, [urlTab, activeSlug, defaultSlug, isMetricsEnabled, isSuperUser, navigate]);

  const selectedIndex = tabs.indexOf(activeLabel);

  return (
    <PageContent data-testid="system-setup-page">
      <PageHeader title="System Setup" divider />

      <PageSection>
        <Tabs
          data-testid="system-setup-tabs"
          options={tabs}
          selected={selectedIndex >= 0 ? selectedIndex : 0}
          onSelect={(idx: number) => {
            const slug = slugFromLabel(tabs[idx]);
            navigate(`/settings/admin/system-setup/${slug}`);
          }}
          borderbottom
        />

        {activeLabel === 'Metrics' && isMetricsEnabled && (
          <PageSection>
            <PerformanceMetricsManager />
          </PageSection>
        )}

        {activeLabel === 'AI Settings' && (
          <PageSection>
            <AIPromptEditor />
          </PageSection>
        )}

        {activeLabel === 'Features' && isSuperUser && (
          <PageSection padtop>
            <FeatureFlagList />
          </PageSection>
        )}
      </PageSection>
    </PageContent>
  );
};

export default SystemSetup;
